import { useDistance, useTaxName } from '@parcel2go/npm-p2g-app';
import { Trans, useTranslation } from 'react-i18next';
import { PickupLocationProximityThresholdMeters } from '~/apps/smartsend/lib/helpers/useQuoteHelper';
import { TranslationNamespaces } from '~/lib/constants';
import { sanitiseKey } from '~/lib/utils/translation';

interface props {
  error: string;
}

// See ErrorCodeText.tsx
export function ErrorCodeMessage(props: props) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  const taxName = useTaxName();
  const { toDistance } = useDistance();

  if (!props.error) {
    return null;
  }

  switch (props.error) {
    // Parcel Details
    case 'ITEM-WEIGHT':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Please enter the weight of your shipment."
        />
      );
    case 'ITEM-VALUE':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Please enter the value of your shipment."
        />
      );
    case 'ITEM-DESCRIPTION':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Please enter the description of your shipment."
        />
      );
    case 'ITEM-DIMENSIONS':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Please enter the dimensions of your parcel as this is needed for your service selection."
        />
      );
    case 'ITEM-PARCEL-EMPTY':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="One of your parcels has nothing in it. Please say what is inside every parcel, or reduce the number of parcels."
        />
      );
    case 'ITEM-PROHIB-INVALID':
      return (
        <Trans t={t} i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)} defaults="This item is prohibited." />
      );

    // Service
    case 'SERVICE-NO-PICKUP-SHOP-AVAILABLE':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="There are no pickup locations within {{limit}} for your recipient."
          values={{
            limit: toDistance(PickupLocationProximityThresholdMeters, 'small')
          }}
        />
      );
    case 'SERVICE-DDU-UNSUPPORTED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="This service requires you to pay for duties upfront."
        />
      );
    case 'SERVICE-MULTIPARCEL-UNSUPPORTED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Multi-parcel shipments are not available on this service."
        />
      );
    case 'SERVICE-MISSING':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Please choose a service for your shipment."
        />
      );
    case 'SERVICE-UNAVAILABLE':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="We're unable to quote for this shipment. A different service might be able to take this, alternatively check the weight and dimensions are correct."
        />
      );

    case 'SERVICE-COLLECTION-DATE':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="We're unable to collect your shipment on this date, please choose a different day for your collection."
        />
      );

    case 'SERVICE-UNSUPPORTED-MARKETPLACE-TEMU':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Choose a Service supported by Temu. See Temu Seller Center for supported Couriers."
        />
      );

    case 'SERVICE-UNSUPPORTED-MARKETPLACE-TIKTOK':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Choose a Service supported by TikTok. See TikTok Shop Seller Center for supported Couriers."
        />
      );

    // Extras
    case 'EXTRAS-UNAVAILABLE':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Some selected extras are not available on this service."
        />
      );

    // Recipient
    case 'ADDRESS-RECIPIENT-SAMEEMAIL-SHOP-DELIVERY':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Shop deliveries cannot use to the same email address as the sender. Please update the recipient email address or choose a different service."
        />
      );
    case 'ADDRESS-RECIPIENT-SAMEEMAIL-LOCKER-DELIVERY':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Locker deliveries cannot use to the same email address as the sender. Please update the recipient email address or choose a different service."
        />
      );
    case 'ADDRESS-RECIPIENT-SAMEPOSTCODE':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="This parcel is being collected and delivered to the same postcode. Please check your collection and return addresses."
        />
      );
    case 'ADDRESS-RECIPIENT-PHONE-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your recipient's phone number is missing."
        />
      );
    case 'ADDRESS-RECIPIENT-EMAIL-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your recipient's email is missing."
        />
      );
    case 'ADDRESS-RECIPIENT-NAME-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your recipient's name is missing."
        />
      );
    case 'ADDRESS-RECIPIENT-ADDRESSLINE1-REQUIRED':
    case 'ADDRESS-RECIPIENT-ADDRESSLINE2-REQUIRED':
    case 'ADDRESS-RECIPIENT-TOWN-REQUIRED':
    case 'ADDRESS-RECIPIENT-POSTCODE-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your recipient's address is missing some details."
        />
      );
    case 'ADDRESS-RECIPIENT-POSTCODE-FORMAT':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your recipient's postcode is not in the correct format for this service."
        />
      );
    case 'ADDRESS-RECIPIENT-PHONE-FORMAT':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your recipient's phone number is not in the correct format for this service."
        />
      );
    case 'ADDRESS-RECIPIENT-PHONE-MIN-LENGTH':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your recipient's phone number is too short."
        />
      );
    case 'ADDRESS-RECIPIENT-PHONE-MAX-LENGTH':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your recipient's phone number is too long."
        />
      );
    case 'ADDRESS-RECIPIENT-EMAIL-FORMAT':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your recipient's email address is not in the correct format."
        />
      );
    case 'ADDRESS-RECIPIENT-COUNTY-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your recipient's county/state is missing."
        />
      );

    case 'ADDRESS-RECIPIENT-COUNTY-INVALID-OPTION':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your recipient's county needs confirming."
        />
      );

    // Sender
    case 'ADDRESS-SENDER-SAMEPOSTCODE':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="This parcel is being collected and delivered to the same postcode."
        />
      );
    case 'ADDRESS-SENDER-PHONE-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your phone number is missing."
        />
      );
    case 'ADDRESS-SENDER-EMAIL-REQUIRED':
      return (
        <Trans t={t} i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)} defaults="Your email is missing." />
      );
    case 'ADDRESS-SENDER-NAME-REQUIRED':
      return <Trans t={t} i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)} defaults="Your name is missing." />;
    case 'ADDRESS-SENDER-ADDRESSLINE1-REQUIRED':
    case 'ADDRESS-SENDER-ADDRESSLINE2-REQUIRED':
    case 'ADDRESS-SENDER-TOWN-REQUIRED':
    case 'ADDRESS-SENDER-POSTCODE-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your address is missing some details."
        />
      );
    case 'ADDRESS-SENDER-POSTCODE-FORMAT':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your postcode is not in the correct format for this service."
        />
      );
    case 'ADDRESS-SENDER-PHONE-FORMAT':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your phone number is not in the correct format for this service."
        />
      );
    case 'ADDRESS-SENDER-PHONE-MIN-LENGTH':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your phone number is too short."
        />
      );
    case 'ADDRESS-SENDER-PHONE-MAX-LENGTH':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your phone number is too long."
        />
      );
    case 'ADDRESS-SENDER-EMAIL-FORMAT':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your email address is not in the correct format."
        />
      );
    case 'ADDRESS-SENDER-COUNTY-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your county/state is missing."
        />
      );

    case 'ADDRESS-SENDER-COUNTY-INVALID-OPTION':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your county needs confirming."
        />
      );

    // Customs
    case 'CUSTOMS-RECIPIENT-TAXID-FORMAT':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your recipient's TAX Number is not in the correct format."
        />
      );

    case 'CUSTOMS-SENDER-TAXID-FORMAT':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your Sender's TAX Number is not in the correct format."
        />
      );

    case 'PRODUCT-TARIFFCODE-TOO-SHORT':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your Tariff Code is missing some digits."
        />
      );

    case 'CUSTOMS-RECIPIENT-UKIMS-FORMAT':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your recipient's UKIMS Number is not in the correct format."
        />
      );

    case 'CUSTOMS-SENDER-UKIMS-FORMAT':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your Sender's UKIMS Number is not in the correct format."
        />
      );

    case 'CUSTOMS-SENDER-EORINUMBER-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your EORI Number is required."
        />
      );

    case 'CUSTOMS-RECIPIENT-EORINUMBER-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your recipient's EORI Number is required."
        />
      );

    case 'CUSTOMS-RECIPIENT-VATNUMBER-OR-TAXID-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults={`Your recipient's {{taxName}} Number or Tax Id is required.`}
          values={{ taxName: taxName }}
        />
      );

    case 'CUSTOMS-RECIPIENT-VATNUMBER-REQUIRED':
    case 'CUSTOMS-RECIPIENT-VATNUMBER-CONDITIONALLY-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults={`Your recipient's {{taxName}} Number is required.`}
          values={{ taxName: taxName }}
        />
      );

    case 'CUSTOMS-RECIPIENT-TAXID-REQUIRED':
    case 'CUSTOMS-RECIPIENT-TAXID-CONDITIONALLY-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your recipient's Tax Number is required."
        />
      );

    case 'CUSTOMS-SENDER-TAXID-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your Tax Number is required."
        />
      );

    case 'CUSTOMS-RECIPIENT-UKIMS-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your recipient's UKIMS Number is required."
        />
      );

    case 'CUSTOMS-SENDER-UKIMS-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your UKIMS Number is required."
        />
      );

    case 'CUSTOMS-SENDER-VATNUMBER-OR-TAXID-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults={`Your {{taxName}} Number or Tax Id is required.`}
          values={{ taxName: taxName }}
        />
      );

    case 'CUSTOMS-SENDER-VATNUMBER-REQUIRED':
    case 'CUSTOMS-SENDER-VATNUMBER-CONDITIONALLY-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults={`Your {{taxName}} Number is required.`}
          values={{ taxName: taxName }}
        />
      );

    case 'CUSTOMS-SENDER-VATNUMBER-FORMAT':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults={`Your {{taxName}} Number is not in the correct format.`}
          values={{ taxName: taxName }}
        />
      );

    case 'CUSTOMS-RECIPIENT-VATSTATUS-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your recipient's Tax Status is required."
        />
      );

    case 'CUSTOMS-SENDER-VATSTATUS-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Your Tax Status is required."
        />
      );

    case 'PRODUCT-TARIFFCODE-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Please provide tariff codes for this shipment."
        />
      );

    case 'ITEM-EXPORTREASON-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Please provide an Export Reason for this item."
        />
      );

    case 'ITEM-IOSSNUMBER-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Please provide an IOSS number for this item."
        />
      );

    case 'ITEM-IOSSNUMBER-FORMAT':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="The IOSS number is not in the correct format for this item."
        />
      );

    case 'PRODUCT-COUNTRYOFMANUFACTURE-REQUIRED':
      return (
        <Trans
          t={t}
          i18nKey={sanitiseKey(`ERROR_DESCRIPTION_${props.error}`)}
          defaults="Please provide a Country of Manufacture for this item."
        />
      );
  }

  return <Trans t={t} i18nKey={'ERROR_DESCRIPTION_DEFAULT'} defaults="Please resolve:" />;
}
